import moment from 'moment-timezone';
import { ParticipantViewModel } from 'stageclip-common';

export interface ParticipantWarningSettings {
  minimumVideoLength?: number;
  maximumVideoLength?: number;
  maximumNumberOfCharactersInName?: number;
  maximumNumberOfCharactersInSubtitle?: number;
}

const TIMESTAMP_FORMAT = 'HH:mm:ss';
const WARNING_TIMESTAMPS_NOT_OVERLAPPING = 'The clipped times do not overlap.';
const WARNING_VIDEO_TOO_SHORT = 'The clipped video appears to be too short.';
const WARNING_VIDEO_TOO_LONG = 'The clipped video appears to be too long.';
const WARNING_FULL_NAME_TOO_LONG = 'Participant name is too long. This may cause issues in the generated clip.';
const WARNING_SUBTITLE_TOO_LONG =
  'The participant {subtitleNumber} subtitle is too long. This may cause issues in the generated clip.';

function parseSubtitleTooLongWarning(subtitleNumber = ''): string {
  return WARNING_SUBTITLE_TOO_LONG.replace('{subtitleNumber}', subtitleNumber);
}

function getDetailsWarnings(participant: ParticipantViewModel, warningSettings: ParticipantWarningSettings): string[] {
  const warnings: string[] = [];

  if (!warningSettings) {
    return warnings;
  }

  if (
    warningSettings.maximumNumberOfCharactersInName &&
    participant.fullName.length > warningSettings.maximumNumberOfCharactersInName
  ) {
    warnings.push(WARNING_FULL_NAME_TOO_LONG);
  }

  if (
    warningSettings.maximumNumberOfCharactersInSubtitle &&
    participant.subtitle?.length > warningSettings.maximumNumberOfCharactersInSubtitle
  ) {
    warnings.push(parseSubtitleTooLongWarning());
  }

  if (
    warningSettings.maximumNumberOfCharactersInSubtitle &&
    participant.secondSubtitle?.length > warningSettings.maximumNumberOfCharactersInSubtitle
  ) {
    warnings.push(parseSubtitleTooLongWarning('second'));
  }

  return warnings;
}

function isTimestampSet(timestamp: string) {
  return !(timestamp === null || timestamp === undefined || timestamp === '');
}

function areTimestampsSet(participant: ParticipantViewModel) {
  return (
    isTimestampSet(participant.audioStartTimestamp) &&
    isTimestampSet(participant.audioEndTimestamp) &&
    isTimestampSet(participant.clipStartTimestamp) &&
    isTimestampSet(participant.clipEndTimestamp)
  );
}

function areTimestampsOverlapping(
  audioStartTimestamp: number,
  audioEndTimestamp: number,
  videoStartTimestamp: number,
  videoEndTimestamp: number,
) {
  return (
    (audioStartTimestamp <= videoStartTimestamp && videoStartTimestamp <= audioEndTimestamp) ||
    (audioStartTimestamp <= videoEndTimestamp && videoEndTimestamp <= audioEndTimestamp)
  );
}

function clippedVideoLength(videoStartTimestamp: number, videoEndTimestamp: number) {
  // the timestamps are in milliseconds
  return (videoEndTimestamp - videoStartTimestamp) / 1000;
}

function getTimestampWarnings(
  participant: ParticipantViewModel,
  warningSettings: ParticipantWarningSettings,
): string[] {
  const warnings: string[] = [];
  const audioStartTimestamp = moment(participant.audioStartTimestamp, TIMESTAMP_FORMAT).valueOf();
  const audioEndTimestamp = moment(participant.audioEndTimestamp, TIMESTAMP_FORMAT).valueOf();
  const videoStartTimestamp = moment(participant.clipStartTimestamp, TIMESTAMP_FORMAT).valueOf();
  const videoEndTimestamp = moment(participant.clipEndTimestamp, TIMESTAMP_FORMAT).valueOf();

  if (!areTimestampsOverlapping(audioStartTimestamp, audioEndTimestamp, videoStartTimestamp, videoEndTimestamp)) {
    warnings.push(WARNING_TIMESTAMPS_NOT_OVERLAPPING);
  }

  if (!warningSettings) {
    return warnings;
  }

  if (
    warningSettings.minimumVideoLength &&
    clippedVideoLength(videoStartTimestamp, videoEndTimestamp) < warningSettings.minimumVideoLength
  ) {
    warnings.push(WARNING_VIDEO_TOO_SHORT);
  }

  if (
    warningSettings.maximumVideoLength &&
    clippedVideoLength(videoStartTimestamp, videoEndTimestamp) > warningSettings.maximumVideoLength
  ) {
    warnings.push(WARNING_VIDEO_TOO_LONG);
  }

  return warnings;
}

export function getParticipantWarnings(participant: ParticipantViewModel, warningSettings: ParticipantWarningSettings) {
  const warnings: string[] = [];

  if (areTimestampsSet(participant)) {
    const timestampWarnings = getTimestampWarnings(participant, warningSettings);
    warnings.push(...timestampWarnings);
  }
  const detailsWarnings = getDetailsWarnings(participant, warningSettings);
  warnings.push(...detailsWarnings);

  return warnings;
}
